// ✅ StoreRequisitionListing.jsx
// ✅ New standardized design
// ✅ Existing API flow preserved
// ✅ Search flow preserved
// ✅ Edit navigation preserved
// ✅ Add New preserved
// ✅ Status styling preserved
// ✅ Dark mode + skin preserved

import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import axios from "axios";
import { useNavigate } from "react-router-dom";

import LayoutWrapper from "../../../components/LayoutWrapper";
import HeaderBar from "../../../components/HeaderBar";

import {
  FaPlus,
  FaSearch,
  FaFilter,
  FaArrowLeft,
  FaClipboardList,
  FaWarehouse,
  FaTimes,
  FaCog,
  FaInfoCircle,
  FaEye,
  FaClock,
  FaCheckCircle,
  FaTimesCircle,
} from "react-icons/fa";

import {
  ToastContainer,
  toast,
} from "react-toastify";

import "react-toastify/dist/ReactToastify.css";

import { ThemeContext } from "../../../context/ThemeContext";
import getCurrentSkin from "../../../utils/getCurrentSkin";

/* =========================================================
   API
========================================================= */

const API_BASE =
  "https://nissi-india.com/api/storeReq";

/* =========================================================
   HELPERS
========================================================= */

const toStr = (value) =>
  String(value ?? "").trim();

const getDefaultFromDate = () => {
  const date = new Date();

  date.setDate(
    date.getDate() - 7
  );

  return date
    .toISOString()
    .split("T")[0];
};

const getTodayDate = () =>
  new Date()
    .toISOString()
    .split("T")[0];

/* =========================================================
   COMPONENT
========================================================= */

const StoreRequisitionListing =
  () => {
    const navigate =
      useNavigate();

    /* =======================================================
       THEME
    ======================================================= */

    const {
      isDark,
      skin,
      setSkin,
      theme,
    } = useContext(
      ThemeContext
    );

    const currentSkin =
      useMemo(
        () =>
          getCurrentSkin(
            theme || skin
          ),
        [
          theme,
          skin,
        ]
      );

    const baseColor =
      currentSkin?.baseColor ||
      "blue";

    const baseShade =
      currentSkin?.baseShade ||
      "600";

    /* =======================================================
       LOCAL STORAGE
    ======================================================= */

    const finYearId =
      localStorage.getItem(
        "finYearId"
      ) || "";

    const finYear =
      localStorage.getItem(
        "finYear"
      ) || "";

    const branchId =
      localStorage.getItem(
        "branchId"
      ) ||
      localStorage.getItem(
        "branchid"
      ) ||
      "";

    const branch =
      localStorage.getItem(
        "branchType"
      ) ||
      localStorage.getItem(
        "branchtype"
      ) ||
      "";

    const userId =
      localStorage.getItem(
        "userId"
      ) ||
      localStorage.getItem(
        "loginuserid"
      ) ||
      "";

    /* =======================================================
       HEADERS
    ======================================================= */

    const commonHeaders =
      useMemo(
        () => ({
          "x-finyearid":
            finYearId,

          "x-finyear":
            finYear,

          "x-branchid":
            branchId,

          "x-branch":
            branch,

          "x-userid":
            userId,
        }),
        [
          finYearId,
          finYear,
          branchId,
          branch,
          userId,
        ]
      );

    /* =======================================================
       DESIGN CLASSES
    ======================================================= */

    const inputClass = `w-full px-3 py-2.5 rounded-lg border text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-${baseColor}-500/30 focus:border-${baseColor}-500 ${
      isDark
        ? "bg-slate-800/60 border-slate-600/50 text-white placeholder-slate-400"
        : "bg-white border-slate-300 text-slate-900 placeholder-slate-400"
    }`;

    const labelClass = `block text-xs font-semibold tracking-wide mb-1.5 ${
      isDark
        ? "text-slate-300"
        : "text-slate-700"
    }`;

    const cardClass = `rounded-2xl shadow-xl border overflow-hidden ${
      isDark
        ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10"
        : `bg-white ${
            currentSkin?.glass ||
            ""
          } border-gray-200`
    }`;

    const neutralButtonClass = `inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition ${
      isDark
        ? "border-slate-600 text-slate-300 hover:bg-slate-800"
        : "border-slate-300 text-slate-700 hover:bg-slate-100"
    }`;

    const tableRowClass = `transition-colors ${
      isDark
        ? "bg-gray-800/20 hover:bg-gray-700/30"
        : "bg-white hover:bg-gray-50"
    }`;

    const tableCellClass = `px-4 py-3 text-sm whitespace-nowrap ${
      isDark
        ? "text-gray-300"
        : "text-gray-900"
    }`;

    /* =======================================================
       STATE
    ======================================================= */

    const [
      showFilters,
      setShowFilters,
    ] = useState(true);

    const [
      loadingStores,
      setLoadingStores,
    ] = useState(false);

    const [
      loadingList,
      setLoadingList,
    ] = useState(false);

    /* -------------------------------------------------------
       FILTERS
    ------------------------------------------------------- */

    const [
      fromDate,
      setFromDate,
    ] = useState(
      getDefaultFromDate
    );

    const [
      toDate,
      setToDate,
    ] = useState(
      getTodayDate
    );

    const [
      scrNo,
      setScrNo,
    ] = useState("");

    const [
      store,
      setStore,
    ] = useState("");

    const [
      status,
      setStatus,
    ] = useState("");

    /* -------------------------------------------------------
       DATA
    ------------------------------------------------------- */

    const [
      storeList,
      setStoreList,
    ] = useState([]);

    const [
      data,
      setData,
    ] = useState([]);

    /* -------------------------------------------------------
       CLIENT PAGINATION
    ------------------------------------------------------- */

    const [
      currentPage,
      setCurrentPage,
    ] = useState(1);

    const [
      pageSize,
      setPageSize,
    ] = useState(10);

    /* =======================================================
       LOAD STORE DROPDOWN
    ======================================================= */

    const loadStores =
      useCallback(
        async () => {
          if (!branchId) {
            toast.error(
              "BranchId not found in localStorage"
            );

            return;
          }

          setLoadingStores(
            true
          );

          try {
            const response =
              await axios.get(
                `${API_BASE}/si_ddlfiledata`,
                {
                  params: {
                    branchid:
                      branchId,
                  },

                  headers:
                    commonHeaders,
                }
              );

            setStoreList(
              Array.isArray(
                response.data
                  ?.stores
              )
                ? response.data
                    .stores
                : []
            );
          } catch (error) {
            console.error(
              "Store dropdown load error:",
              error
            );

            toast.error(
              error?.response
                ?.data
                ?.message ||
                "Failed to load store dropdown"
            );

            setStoreList(
              []
            );
          } finally {
            setLoadingStores(
              false
            );
          }
        },
        [
          branchId,
          commonHeaders,
        ]
      );

    /* =======================================================
       INITIAL LOAD
    ======================================================= */

    useEffect(() => {
      loadStores();
    }, [
      loadStores,
    ]);

    /* =======================================================
       SEARCH
    ======================================================= */

    const handleSearch =
      async () => {
        if (
          !fromDate ||
          !toDate ||
          !status
        ) {
          toast.warning(
            "Please Select Status, From date and To date"
          );

          return;
        }

        if (!branchId) {
          toast.error(
            "BranchId not found in localStorage"
          );

          return;
        }

        if (
          new Date(
            fromDate
          ).getTime() >
          new Date(
            toDate
          ).getTime()
        ) {
          toast.warning(
            "From Date should not be greater than To Date"
          );

          return;
        }

        setLoadingList(
          true
        );

        try {
          const response =
            await axios.get(
              `${API_BASE}/si_grid`,
              {
                params: {
                  branchid:
                    branchId,

                  reqno:
                    scrNo ||
                    "",

                  dtfrom:
                    fromDate,

                  dtto:
                    toDate,

                  fromstore:
                    store ||
                    "%",

                  status,
                },

                headers:
                  commonHeaders,
              }
            );

          const rows =
            Array.isArray(
              response.data
                ?.data
            )
              ? response.data
                  .data
              : [];

          setData(
            rows
          );

          setCurrentPage(
            1
          );

          if (
            rows.length ===
            0
          ) {
            toast.info(
              "No records found."
            );
          } else {
            toast.success(
              `Found ${rows.length} record(s)`
            );
          }
        } catch (error) {
          console.error(
            "Listing fetch error:",
            error
          );

          toast.error(
            error?.response
              ?.data
              ?.message ||
              "Failed to fetch listing"
          );

          setData(
            []
          );
        } finally {
          setLoadingList(
            false
          );
        }
      };

    /* =======================================================
       CLEAR FILTERS
    ======================================================= */

    const clearFilters =
      () => {
        setScrNo(
          ""
        );

        setFromDate(
          getDefaultFromDate()
        );

        setToDate(
          getTodayDate()
        );

        setStatus(
          ""
        );

        setStore(
          ""
        );

        setData(
          []
        );

        setCurrentPage(
          1
        );

        toast.info(
          "Filters cleared"
        );
      };

    /* =======================================================
       ROW NAVIGATION
    ======================================================= */

    const handleRowClick =
      (row) => {
        navigate(
          "/inventory/StoreRequisition",
          {
            state: {
              storeRequestId:
                row.fld_StoreRequestid,

              storeRequestNo:
                row.fld_StoreRequestNo,

              status:
                row.status,
            },
          }
        );
      };

    /* =======================================================
       STATUS
    ======================================================= */

    const getStatusBadgeClass =
      (rowStatus) => {
        const value =
          toStr(
            rowStatus
          ).toLowerCase();

        if (
          value ===
          "waiting"
        ) {
          return isDark
            ? "bg-amber-900/30 text-amber-300 border border-amber-700/50"
            : "bg-amber-50 text-amber-700 border border-amber-200";
        }

        if (
          value ===
            "close" ||
          value ===
            "closed"
        ) {
          return isDark
            ? "bg-emerald-900/30 text-emerald-300 border border-emerald-700/50"
            : "bg-emerald-50 text-emerald-700 border border-emerald-200";
        }

        if (
          value ===
            "cancel" ||
          value ===
            "cancelled" ||
          value ===
            "canceled"
        ) {
          return isDark
            ? "bg-red-900/30 text-red-300 border border-red-700/50"
            : "bg-red-50 text-red-700 border border-red-200";
        }

        return isDark
          ? `bg-${baseColor}-900/30 text-${baseColor}-300 border border-${baseColor}-700/50`
          : `bg-${baseColor}-50 text-${baseColor}-700 border border-${baseColor}-200`;
      };

    const getStatusIcon =
      (rowStatus) => {
        const value =
          toStr(
            rowStatus
          ).toLowerCase();

        if (
          value ===
          "waiting"
        ) {
          return (
            <FaClock
              size={11}
            />
          );
        }

        if (
          value ===
            "close" ||
          value ===
            "closed"
        ) {
          return (
            <FaCheckCircle
              size={11}
            />
          );
        }

        if (
          value ===
            "cancel" ||
          value ===
            "cancelled" ||
          value ===
            "canceled"
        ) {
          return (
            <FaTimesCircle
              size={11}
            />
          );
        }

        return null;
      };

    /* =======================================================
       PAGINATION
    ======================================================= */

    const totalPages =
      Math.max(
        1,
        Math.ceil(
          data.length /
            pageSize
        )
      );

    const indexOfLastItem =
      currentPage *
      pageSize;

    const indexOfFirstItem =
      indexOfLastItem -
      pageSize;

    const currentItems =
      useMemo(
        () =>
          data.slice(
            indexOfFirstItem,
            indexOfLastItem
          ),
        [
          data,
          indexOfFirstItem,
          indexOfLastItem,
        ]
      );

    const paginationPages =
      useMemo(() => {
        if (
          totalPages <= 5
        ) {
          return Array.from(
            {
              length:
                totalPages,
            },
            (
              _,
              index
            ) =>
              index + 1
          );
        }

        if (
          currentPage <=
          3
        ) {
          return [
            1,
            2,
            3,
            4,
            5,
          ];
        }

        if (
          currentPage >=
          totalPages - 2
        ) {
          return [
            totalPages -
              4,
            totalPages -
              3,
            totalPages -
              2,
            totalPages -
              1,
            totalPages,
          ];
        }

        return [
          currentPage -
            2,
          currentPage -
            1,
          currentPage,
          currentPage +
            1,
          currentPage +
            2,
        ];
      }, [
        currentPage,
        totalPages,
      ]);

    const showingFrom =
      data.length === 0
        ? 0
        : indexOfFirstItem +
          1;

    const showingTo =
      Math.min(
        indexOfLastItem,
        data.length
      );

    const isLoading =
      loadingStores ||
      loadingList;

    /* =======================================================
       RENDER
    ======================================================= */

    return (
      <LayoutWrapper>
        {/* ===================================================
            HEADER
        =================================================== */}

        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">
              STORE ISSUE
              REQUISITION
              LISTING
            </span>
          }
          setSkin={
            setSkin
          }
          currentSkin={
            skin
          }
        />

        <div className="space-y-6 mt-6">
          {/* =================================================
              PAGE HEADER CARD
          ================================================= */}

          <div
            className={
              cardClass
            }
          >
            <div
              className={`px-6 py-4 border-b flex flex-col lg:flex-row lg:items-center justify-between gap-4 ${
                isDark
                  ? "border-slate-700/50 bg-slate-900/40"
                  : "border-slate-200/50 bg-white/60"
              }`}
            >
              {/* LEFT */}

              <div className="flex items-center gap-3">
                <div
                  className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                >
                  <FaClipboardList className="w-5 h-5 text-white" />
                </div>

                <div>
                  <div className="flex flex-wrap items-center gap-3">
                    <h3
                      className={`text-lg font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      Store
                      Requisition
                      Listing
                    </h3>

                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        isDark
                          ? "bg-gray-700 text-gray-300"
                          : "bg-gray-100 text-gray-600"
                      }`}
                    >
                      {
                        data.length
                      }{" "}
                      Records
                    </span>

                    {isLoading && (
                      <span
                        className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${
                          isDark
                            ? "bg-gray-700 text-gray-300"
                            : `bg-${baseColor}-50 text-${baseColor}-700`
                        }`}
                      >
                        <FaCog className="animate-spin" />

                        Loading...
                      </span>
                    )}
                  </div>

                  <p
                    className={`text-xs mt-1 ${
                      isDark
                        ? "text-slate-400"
                        : "text-slate-600"
                    }`}
                  >
                    Search and manage
                    store issue
                    requisition records
                  </p>
                </div>
              </div>

              {/* RIGHT */}

              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setShowFilters(
                      (
                        previous
                      ) =>
                        !previous
                    )
                  }
                  className={
                    neutralButtonClass
                  }
                >
                  <FaFilter />

                  {showFilters
                    ? "Hide Filters"
                    : "Show Filters"}
                </button>

                <button
                  type="button"
                  onClick={() =>
                    navigate(-1)
                  }
                  className={
                    neutralButtonClass
                  }
                >
                  <FaArrowLeft />

                  Back
                </button>

                <button
                  type="button"
                  onClick={() =>
                    navigate(
                      "/inventory/StoreRequisition"
                    )
                  }
                  className={`inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                >
                  <FaPlus />

                  Add New
                </button>
              </div>
            </div>
          </div>

          {/* =================================================
              FILTER CARD
          ================================================= */}

          {showFilters && (
            <div
              className={
                cardClass
              }
            >
              {/* FILTER HEADER */}

              <div
                className={`px-6 py-4 border-b ${
                  isDark
                    ? "border-slate-700/50 bg-slate-900/40"
                    : "border-slate-200/50 bg-white/60"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-${baseColor}-500 to-${baseColor}-600 shadow-md`}
                  >
                    <FaFilter className="w-4 h-4 text-white" />
                  </div>

                  <div>
                    <h3
                      className={`text-lg font-bold ${
                        isDark
                          ? "text-white"
                          : "text-slate-900"
                      }`}
                    >
                      Filtration Area
                    </h3>

                    <p
                      className={`text-xs ${
                        isDark
                          ? "text-slate-400"
                          : "text-slate-600"
                      }`}
                    >
                      Filter by request
                      number, date,
                      status and store
                    </p>
                  </div>
                </div>
              </div>

              {/* FILTER BODY */}

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {/* REQUEST NO */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Request No
                    </label>

                    <input
                      type="text"
                      value={
                        scrNo
                      }
                      onChange={(
                        event
                      ) =>
                        setScrNo(
                          event
                            .target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                      placeholder="Enter Request No"
                    />
                  </div>

                  {/* FROM DATE */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      From Date

                      <span className="text-red-500 ml-1">
                        *
                      </span>
                    </label>

                    <input
                      type="date"
                      value={
                        fromDate
                      }
                      onChange={(
                        event
                      ) =>
                        setFromDate(
                          event
                            .target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    />
                  </div>

                  {/* TO DATE */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      To Date

                      <span className="text-red-500 ml-1">
                        *
                      </span>
                    </label>

                    <input
                      type="date"
                      value={
                        toDate
                      }
                      onChange={(
                        event
                      ) =>
                        setToDate(
                          event
                            .target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    />
                  </div>

                  {/* STATUS */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Status

                      <span className="text-red-500 ml-1">
                        *
                      </span>
                    </label>

                    <select
                      value={
                        status
                      }
                      onChange={(
                        event
                      ) =>
                        setStatus(
                          event
                            .target
                            .value
                        )
                      }
                      className={
                        inputClass
                      }
                    >
                      <option value="">
                        Select Status
                      </option>

                      <option value="All">
                        All
                      </option>

                      <option value="Waiting">
                        Waiting
                      </option>

                      <option value="Cancel">
                        Cancel
                      </option>
                    </select>
                  </div>

                  {/* STORE */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Store
                    </label>

                    <select
                      value={
                        store
                      }
                      onChange={(
                        event
                      ) =>
                        setStore(
                          event
                            .target
                            .value
                        )
                      }
                      disabled={
                        loadingStores
                      }
                      className={
                        inputClass
                      }
                    >
                      <option value="">
                        Select
                      </option>

                      {storeList.map(
                        (
                          storeItem,
                          index
                        ) => (
                          <option
                            key={
                              storeItem.fld_storeid ||
                              index
                            }
                            value={
                              storeItem.fld_storeid
                            }
                          >
                            {
                              storeItem.fld_storename
                            }
                          </option>
                        )
                      )}
                    </select>
                  </div>

                  {/* PAGE SIZE */}

                  <div>
                    <label
                      className={
                        labelClass
                      }
                    >
                      Page Size
                    </label>

                    <select
                      value={
                        pageSize
                      }
                      onChange={(
                        event
                      ) => {
                        setPageSize(
                          Number(
                            event
                              .target
                              .value
                          ) ||
                            10
                        );

                        setCurrentPage(
                          1
                        );
                      }}
                      className={
                        inputClass
                      }
                    >
                      {[
                        10,
                        25,
                        50,
                        100,
                      ].map(
                        (
                          size
                        ) => (
                          <option
                            key={
                              size
                            }
                            value={
                              size
                            }
                          >
                            {
                              size
                            }
                          </option>
                        )
                      )}
                    </select>
                  </div>
                </div>

                {/* ACTIONS */}

                <div
                  className={`flex flex-col md:flex-row md:items-center justify-between gap-4 mt-6 pt-5 border-t ${
                    isDark
                      ? "border-slate-700"
                      : "border-slate-200"
                  }`}
                >
                  <button
                    type="button"
                    onClick={
                      clearFilters
                    }
                    className={
                      neutralButtonClass
                    }
                  >
                    <FaTimes />

                    Clear Filters
                  </button>

                  <button
                    type="button"
                    onClick={
                      handleSearch
                    }
                    disabled={
                      loadingList
                    }
                    className={`inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white rounded-xl shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700 disabled:opacity-60 disabled:cursor-not-allowed`}
                  >
                    {loadingList ? (
                      <FaCog className="animate-spin" />
                    ) : (
                      <FaSearch />
                    )}

                    {loadingList
                      ? "Searching..."
                      : "Search Records"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* =================================================
              TABLE CARD
          ================================================= */}

          <div
            className={
              cardClass
            }
          >
            {/* TABLE HEADER */}

            <div
              className={`p-6 border-b ${
                isDark
                  ? "border-gray-700"
                  : "border-gray-200"
              }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`w-3 h-8 rounded-full bg-${baseColor}-${baseShade}`}
                  />

                  <div>
                    <h3
                      className={`text-xl font-bold ${
                        isDark
                          ? "text-white"
                          : "text-gray-900"
                      }`}
                    >
                      Requisition
                      Records
                    </h3>

                    <p
                      className={`text-xs mt-1 ${
                        isDark
                          ? "text-gray-400"
                          : "text-gray-500"
                      }`}
                    >
                      Click Request No
                      or View to open
                      the requisition
                    </p>
                  </div>

                  <span
                    className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      isDark
                        ? "bg-gray-700 text-gray-300"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {
                      data.length
                    }{" "}
                    Records
                  </span>
                </div>

                {data.length >
                  0 && (
                  <span
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold ${
                      isDark
                        ? `bg-${baseColor}-900/30 text-${baseColor}-300`
                        : `bg-${baseColor}-50 text-${baseColor}-700`
                    }`}
                  >
                    Page{" "}
                    {
                      currentPage
                    }{" "}
                    /{" "}
                    {
                      totalPages
                    }
                  </span>
                )}
              </div>
            </div>

            {/* ===============================================
                LOADING
            =============================================== */}

            {loadingList ? (
              <div className="py-16 text-center">
                <FaCog
                  className={`w-10 h-10 mx-auto animate-spin text-${baseColor}-500`}
                />

                <p
                  className={`mt-3 text-sm ${
                    isDark
                      ? "text-gray-400"
                      : "text-gray-500"
                  }`}
                >
                  Loading requisition
                  records...
                </p>
              </div>
            ) : data.length >
              0 ? (
              <>
                {/* ===========================================
                    TABLE
                =========================================== */}

                <div className="overflow-x-auto">
                  <table className="min-w-[1150px] w-full text-sm">
                    <thead
                      className={`bg-gradient-to-r from-${baseColor}-600 to-${baseColor}-700 text-white`}
                    >
                      <tr>
                        <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                          S.No
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Request No
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Date
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Indent Ref
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          From Store
                        </th>

                        <th className="px-4 py-3 text-left text-xs font-bold uppercase whitespace-nowrap">
                          Employee
                        </th>

                        <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                          Status
                        </th>

                        <th className="px-4 py-3 text-center text-xs font-bold uppercase whitespace-nowrap">
                          Action
                        </th>
                      </tr>
                    </thead>

                    <tbody
                      className={`divide-y ${
                        isDark
                          ? "divide-gray-700"
                          : "divide-gray-200"
                      }`}
                    >
                      {currentItems.map(
                        (
                          row,
                          index
                        ) => (
                          <tr
                            key={`${row.fld_StoreRequestid || row.fld_StoreRequestNo || "req"}-${index}`}
                            className={
                              tableRowClass
                            }
                          >
                            {/* S.NO */}

                            <td
                              className={`${tableCellClass} text-center font-medium`}
                            >
                              {indexOfFirstItem +
                                index +
                                1}
                            </td>

                            {/* REQUEST NO */}

                            <td className="px-4 py-3 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() =>
                                  handleRowClick(
                                    row
                                  )
                                }
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                                  isDark
                                    ? `bg-${baseColor}-900/30 text-${baseColor}-300 hover:bg-${baseColor}-900/50`
                                    : `bg-${baseColor}-50 text-${baseColor}-700 hover:bg-${baseColor}-100`
                                }`}
                              >
                                {
                                  row.fld_StoreRequestNo ||
                                  "-"
                                }
                              </button>
                            </td>

                            {/* DATE */}

                            <td
                              className={
                                tableCellClass
                              }
                            >
                              {row.ReqDate ||
                                "-"}
                            </td>

                            {/* INDENT */}

                            <td
                              className={
                                tableCellClass
                              }
                            >
                              {row.fld_Indentref ||
                                "-"}
                            </td>

                            {/* STORE */}

                            <td
                              className={
                                tableCellClass
                              }
                            >
                              <div className="flex items-center gap-2">
                                <FaWarehouse
                                  className={`text-${baseColor}-500`}
                                />

                                <span>
                                  {row.fromstore ||
                                    "-"}
                                </span>
                              </div>
                            </td>

                            {/* EMPLOYEE */}

                            <td
                              className={
                                tableCellClass
                              }
                            >
                              {row.fld_Employee ||
                                "-"}
                            </td>

                            {/* STATUS */}

                            <td className="px-4 py-3 text-center whitespace-nowrap">
                              <span
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${getStatusBadgeClass(
                                  row.status
                                )}`}
                              >
                                {getStatusIcon(
                                  row.status
                                )}

                                {row.status ||
                                  "-"}
                              </span>
                            </td>

                            {/* ACTION */}

                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() =>
                                  handleRowClick(
                                    row
                                  )
                                }
                                title="View / Edit Requisition"
                                className={`inline-flex items-center justify-center w-9 h-9 rounded-lg text-white shadow transition bg-${baseColor}-600 hover:bg-${baseColor}-700`}
                              >
                                <FaEye
                                  size={
                                    14
                                  }
                                />
                              </button>
                            </td>
                          </tr>
                        )
                      )}
                    </tbody>
                  </table>
                </div>

                {/* ===========================================
                    PAGINATION
                =========================================== */}

                <div
                  className={`p-6 border-t ${
                    isDark
                      ? "border-gray-700"
                      : "border-gray-200"
                  }`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div
                      className={`text-sm ${
                        isDark
                          ? "text-gray-300"
                          : "text-gray-700"
                      }`}
                    >
                      Showing{" "}
                      {
                        showingFrom
                      }{" "}
                      to{" "}
                      {
                        showingTo
                      }{" "}
                      of{" "}
                      {
                        data.length
                      }{" "}
                      results
                    </div>

                    <div
                      className={`inline-flex rounded-lg shadow-sm overflow-hidden border ${
                        isDark
                          ? "border-gray-700 bg-gray-900"
                          : "border-gray-300 bg-white"
                      }`}
                    >
                      {/* PREVIOUS */}

                      <button
                        type="button"
                        onClick={() =>
                          setCurrentPage(
                            (
                              previous
                            ) =>
                              Math.max(
                                1,
                                previous -
                                  1
                              )
                          )
                        }
                        disabled={
                          currentPage ===
                          1
                        }
                        className={`px-4 py-2 text-sm font-medium transition ${
                          currentPage ===
                          1
                            ? "text-gray-400 cursor-not-allowed"
                            : isDark
                            ? "text-gray-200 hover:bg-gray-800"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        Previous
                      </button>

                      {/* NUMBERS */}

                      {paginationPages.map(
                        (
                          pageNumber
                        ) => (
                          <button
                            type="button"
                            key={
                              pageNumber
                            }
                            onClick={() =>
                              setCurrentPage(
                                pageNumber
                              )
                            }
                            className={`px-4 py-2 text-sm font-medium transition ${
                              currentPage ===
                              pageNumber
                                ? `bg-${baseColor}-${baseShade} text-white`
                                : isDark
                                ? "text-gray-200 hover:bg-gray-800"
                                : "text-gray-700 hover:bg-gray-100"
                            }`}
                          >
                            {
                              pageNumber
                            }
                          </button>
                        )
                      )}

                      {/* NEXT */}

                      <button
                        type="button"
                        onClick={() =>
                          setCurrentPage(
                            (
                              previous
                            ) =>
                              Math.min(
                                totalPages,
                                previous +
                                  1
                              )
                          )
                        }
                        disabled={
                          currentPage ===
                          totalPages
                        }
                        className={`px-4 py-2 text-sm font-medium transition ${
                          currentPage ===
                          totalPages
                            ? "text-gray-400 cursor-not-allowed"
                            : isDark
                            ? "text-gray-200 hover:bg-gray-800"
                            : "text-gray-700 hover:bg-gray-100"
                        }`}
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              /* =============================================
                  EMPTY
              ============================================= */

              <div className="text-center py-14">
                <div
                  className={`inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 ${
                    isDark
                      ? "bg-gray-800"
                      : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-8 h-8 text-gray-400" />
                </div>

                <h3
                  className={`font-semibold ${
                    isDark
                      ? "text-gray-200"
                      : "text-gray-800"
                  }`}
                >
                  No Requisition
                  Records Found
                </h3>

                <p
                  className={`text-sm mt-1 ${
                    isDark
                      ? "text-gray-400"
                      : "text-gray-500"
                  }`}
                >
                  Select the required
                  filters and click
                  Search Records.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* ===================================================
            TOAST
        =================================================== */}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={
            isDark
              ? "dark"
              : "light"
          }
        />
      </LayoutWrapper>
    );
  };

export default StoreRequisitionListing;